/*
 * swatch-image-sync.js
 *
 * Keeps colour-swatch thumbnails in sync with the shopper's OTHER option
 * selections (e.g. a Color swatch row that should follow the selected Shape).
 *
 * Why this exists: the theme's <variant-selects> re-renders price / SKU /
 * inventory / media on a variant change, but it does NOT re-render the swatch
 * markup itself (renderProductInfo only swaps a fixed set of ids, and the
 * client-side availability pass skips option position 1 entirely). So each
 * swatch's server-rendered <img src> would otherwise stay frozen at its
 * page-load value. There is no HTMLUpdateUtility.viewTransition in this theme
 * despite older comments claiming so — the picker is never replaced — so the
 * only way to keep swatch images live is to recompute them on the client.
 *
 * Resolution mirrors snippets/product-variant-options.liquid exactly:
 *   pick the variant where option[thisPosition] == swatchValue AND every other
 *   option == the currently selected value; fall back to the first variant
 *   carrying the swatch value at this position (sparse matrices / deleted
 *   variants) so a swatch never goes blank.
 */
(function () {
  if (window.__swatchImageSyncLoaded) return;
  window.__swatchImageSyncLoaded = true;

  var IMAGE_WIDTH = 200; // matches `image_url: width: 200` in the Liquid

  function sizedUrl(src, width) {
    if (!src) return src;
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.href;
    } catch (e) {
      return src;
    }
  }

  // Compare only the CDN file path (ignore ?v= / ?width= differences) so an
  // initial pass never rewrites an <img> that already shows the right image —
  // the Liquid-emitted URL and the JS-built one can differ in query formatting
  // while pointing at the same file. Only a genuinely different image rewrites.
  function sameImage(a, b) {
    try {
      return (
        new URL(a, window.location.origin).pathname ===
        new URL(b, window.location.origin).pathname
      );
    } catch (e) {
      return a === b;
    }
  }

  function getVariants(root) {
    var script = root.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  // Currently-selected value of each option group, in option order.
  function currentSelections(groups) {
    return groups.map(function (group) {
      var select = group.querySelector('.select__select');
      if (select) return select.value;
      var checked = group.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  function resolveVariant(variants, position, value, selections) {
    var idx = position - 1;

    // Exact match: this option == value AND every other option == current.
    var exact = variants.find(function (variant) {
      if (!variant.options) return false;
      for (var i = 0; i < selections.length; i++) {
        var want = i === idx ? value : selections[i];
        if (want != null && variant.options[i] !== want) return false;
      }
      return true;
    });
    if (exact) return exact;

    // Fallback: first variant carrying `value` at this position.
    return (
      variants.find(function (variant) {
        return variant.options && variant.options[idx] === value;
      }) || null
    );
  }

  function applyToSwatch(swatch, position, variants, selections) {
    var input = swatch.querySelector('input[data-swatch="true"]');
    if (!input) return;

    var holder = swatch.querySelector('.color-swatch__image');
    if (!holder) return;

    var mode = holder.getAttribute('data-swatch-image-mode');
    if (mode !== 'disabled' && mode !== 'image_alt') return; // static modes never change

    var variant = resolveVariant(variants, position, input.value, selections);
    if (!variant) return;
    var featured = variant.featured_image;

    if (mode === 'disabled') {
      var img = holder.querySelector('img');
      if (featured && featured.src) {
        if (!img) {
          img = document.createElement('img');
          img.setAttribute('width', 'auto');
          img.setAttribute('height', 'auto');
          img.setAttribute('loading', 'lazy');
          img.alt = input.value;
          holder.appendChild(img);
        }
        var next = sizedUrl(featured.src, IMAGE_WIDTH);
        if (!sameImage(img.getAttribute('src') || '', next)) img.setAttribute('src', next);
      }
    } else if (mode === 'image_alt') {
      var custom = holder.querySelector('.color-swatch__custom-color[data-image-alt="true"]');
      if (custom) {
        custom.style.setProperty('--bg-color', featured && featured.alt ? featured.alt : '');
      }
    }
  }

  function syncRoot(root) {
    var variants = getVariants(root);
    if (!variants || !variants.length) return;

    var groups = Array.prototype.slice.call(root.querySelectorAll('.product-form__input'));
    if (!groups.length) return;

    var selections = currentSelections(groups);

    groups.forEach(function (group, groupIndex) {
      var swatches = group.querySelectorAll('.color-swatch');
      if (!swatches.length) return;
      var position = groupIndex + 1; // option groups render in option order
      swatches.forEach(function (swatch) {
        applyToSwatch(swatch, position, variants, selections);
      });
    });
  }

  // Delegated so it survives picker replacement and covers dynamically added
  // pickers (quick-add, secondary sticky pickers) without per-element binding.
  function handleChange(event) {
    // Never let a fault here disturb the theme's own change handlers.
    try {
      var target = event.target;
      if (!target || !target.closest) return;
      var root = target.closest('variant-selects');
      if (root) syncRoot(root);
    } catch (e) {
      /* no-op */
    }
  }

  function init() {
    document.addEventListener('change', handleChange);
    document.querySelectorAll('variant-selects').forEach(syncRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
